import * as React from 'react';
import { Drawer, IconButton } from '@teamsnap/snap-ui';
import usePostRegEditLogs from './usePostRegEditLogs';
import { FormResult } from '../types';
import './PostRegEditLogDrawer.scss';
import { PostRegEditLogDisplay } from './types';
import { getDateString } from './utils';

interface Props {
  formResult: FormResult;
  isOpen: boolean;
  orgId: number;
  setIsOpen: (boolean) => void;
}

const EditLogDisplay: React.FunctionComponent<{
  log: PostRegEditLogDisplay;
  formattedTimestamp: string;
}> = ({ log, formattedTimestamp }) => (
  <li>
    <div
      data-testid={'PostRegEditLogDrawer--updatedAt'}
      className="sui-caption sui-text-neutral-text-medium"
    >
      {formattedTimestamp}
    </div>

    <ul data-testid={'PostRegEditLogDrawer--details'}>
      {log.details.map(({ notes, updatedByDisplay }, i) => (
        <li key={i} className="PostRegEditLogDrawer--detail">
          <b>{updatedByDisplay}</b>{' '}
          <span dangerouslySetInnerHTML={{ __html: notes }} />
        </li>
      ))}
    </ul>
  </li>
);

const PostRegEditLogDrawer: React.FunctionComponent<Props> = ({
  formResult,
  isOpen,
  setIsOpen,
  orgId,
}) => {
  const { editLogs, loading, orgTimezoneIana } = usePostRegEditLogs({
    formResultId: Number(formResult?.formResultId),
    isOpen,
    orgId,
  });
  const onClose = () => setIsOpen(false);

  return (
    <Drawer
      title="View Editing History"
      open={isOpen}
      allowOverlayClose={true}
      onCloseClick={onClose}
      onClickOutside={onClose}
      allowEscapeKey
      size="small"
      showOverlay
      testId="PostRegEditLogDrawer--drawer"
      className="sui-p-0 PostRegEditLogDrawer"
    >
      <Drawer.Header data-testid="PostRegEditLogDrawer--header">
        <span className="sui-heading-sm">View Editing History</span>
        <IconButton
          icon="close"
          onClick={onClose}
          className="sui-ml-auto sui-text-gray-7"
        />
      </Drawer.Header>
      <Drawer.Content
        data-testid="PostRegEditLogDrawer--content"
        className="PostRegEditLogDrawer--body sui-body"
      >
        {loading ? (
          <div>Loading...</div>
        ) : (
          <ul className="PostRegEditLogDrawer--list">
            {editLogs.map((log, i) => (
              <EditLogDisplay
                key={i}
                log={log}
                formattedTimestamp={getDateString(
                  log.updatedAt,
                  orgTimezoneIana
                )}
              />
            ))}
          </ul>
        )}
        {!loading && editLogs.length === 0 && (
          <div data-testid="PostRegEditLogDrawer--empty">No logs found</div>
        )}
      </Drawer.Content>
    </Drawer>
  );
};

export default PostRegEditLogDrawer;
